import { Icon, Tooltip, Typography } from "@/components/atoms";
import { useAppSelector } from "@/redux/hooks";
import { configSelector } from "@/redux/slices/config/selectors";

/**
 * Info tooltip for the "Credit Card Payments" sub-header (Figma 5379-29655):
 * dark panel with the card management & security notes, operator name substituted.
 * Grouped under two headings rather than one flat list — seven undifferentiated
 * bullets in a popover don't get read (Jodie, 2026-09-24).
 */
export const CardSecurityTooltip = () => {
  const operatorName = useAppSelector(configSelector)?.name || "The operator";

  const sections: { heading: string; bullets: { id: string; content: React.ReactNode }[] }[] = [
    {
      heading: "Your card details",
      bullets: [
        {
          id: "stripe-direct",
          content: `${operatorName} does not have access to your credit card details at any time — you are registering your credit card directly with Stripe via a secure encrypted link.`,
        },
        {
          // The old copy told clients to remove the card and add a new one, which the API refuses for
          // any card attached to an upcoming trip. Editing repoints that card's unpaid jobs instead.
          id: "update-card",
          content:
            "To update your credit card details, edit the existing card rather than adding a new one — the updated details carry across to your existing bookings.",
        },
        {
          id: "fraud-checks",
          content: `${operatorName} will perform fraud checks (including a small pre-authorization) upon registering your card to check validity.`,
        },
        {
          id: "stripe-link",
          content: (
            <>
              Please visit Stripe for more information on your card security:{" "}
              <a href="https://stripe.com/" target="_blank" rel="noreferrer" className="underline">
                https://stripe.com/
              </a>
            </>
          ),
        },
      ],
    },
    {
      heading: "When you're charged",
      bullets: [
        {
          // Invoice-billed clients book with no card at all, so this can't claim a card is required.
          id: "method-required",
          content:
            "You need a valid payment method before you can book — a registered credit card, or invoicing if your company is set up for it.",
        },
        {
          id: "pre-trip-payment",
          content: `${operatorName} reserves the right to collect payment prior to a trip for new clients, special and one-off bookings.`,
        },
        {
          id: "wrong-card",
          content: `${operatorName} is not able to reverse payments made to an incorrect card. Please double-check before booking.`,
        },
      ],
    },
  ];

  return (
    <Tooltip
      interactive
      placement="bottom-start"
      maxWidth={560}
      className="!rounded-lg !bg-neutral-black [&>.tippy-arrow]:!text-neutral-black"
      content={
        <div className="flex flex-col gap-2.5 p-1.5 text-sm leading-4 text-white">
          <p className="font-semibold">Manage your cards &amp; credit card security</p>
          {sections.map((section) => (
            <div key={section.heading} className="flex flex-col gap-1">
              <p className="font-semibold text-neutral-mid-gray">{section.heading}</p>
              <ul className="list-disc pl-4 font-medium">
                {section.bullets.map((bullet) => (
                  <li key={bullet.id}>{bullet.content}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      }
    >
      <span tabIndex={0} role="button" className="w-fit cursor-pointer self-start text-neutral-dark-gray">
        <Typography variant="small-regular" className="!inline underline decoration-dotted underline-offset-2">
          How card security &amp; post-trip charges work — read more
        </Typography>{" "}
        <Icon name="InfoCircle" variant="Outline" size={16} className="inline align-middle text-neutral-dark-gray" />
      </span>
    </Tooltip>
  );
};
